<template>
  <div
    v-if="step === 'getEmail'"
    class="w-full"
  >
    <h1 class="text-xl text-center mb-2">
      {{ $t('mycountly.signup.forgot-password') }}
    </h1>
    <p class="text-sm text-center text-neutral-500 mb-6">
      {{ $t('mycountly.signup.please-enter-your-account-email') }}
      {{ $t('mycountly.signup.receive-instructions-on') }}
    </p>
    <cly-request
      disable-loading-icon
      silent-error
      @success="onSendLinkSuccess"
      @error="onSendLinkError"
    >
      <template #default="request">
        <cly-form>
          <template #default="v">
            <cly-form-step
              id="forgotPasswordStep"
              @enter="handleEnter(request, v, 'send')"
            >
              <cly-form-field
                :rules="'required|email|max:320'"
                :label="$t('mycountly.signup.email-address')"
              >
                <el-input
                  v-model="email"
                  type="email"
                  class="w-full mt-1"
                  :placeholder="$t('mycountly.signup.placeholder.enter-your-email')"
                />
              </cly-form-field>
              <myc-big-button
                :disabled="request.pending || !v.isSubmissionAllowed"
                type="success"
                class="w-full !mt-5"
                @clicked="request.submit(getSendLinkSubmission)"
              >
                {{ $t('mycountly.signup.send-email') }}
              </myc-big-button>
            </cly-form-step>
          </template>
        </cly-form>
      </template>
    </cly-request>
    <p class="text-center mt-6 text-neutral-500 text-[13px]">
      <router-link
        class="!text-[#1EAB56] hover:!text-[#178f47]"
        to="/account/signin"
      >
        {{ $t('mycountly.signup.back-to-login') }}
      </router-link>
    </p>
  </div>
  <div
    v-else-if="step === 'mailSent'"
    class="w-full text-center"
  >
    <check-circle-icon class="w-16 h-16 mx-auto" />
    <h1 class="text-xl text-center mt-6 mb-2">
      {{ $t('mycountly.signup.password-reset-email-sent') }}
    </h1>
    <p class="text-sm text-center text-neutral-500">
      {{ $t('mycountly.signup.an-email-has-been-sent', { 0: email }) }}
    </p>
    <p class="text-sm text-center text-neutral-500 mb-6">
      {{ $t('mycountly.signup.follow-directions-in-the-email') }}
    </p>
    <router-link to="/account/signin">
      <myc-big-button
        type="success"
        class="w-full !mt-5"
      >
        {{ $t('mycountly.signup.back-to-login') }}
      </myc-big-button>
    </router-link>
  </div>
  <div
    v-else
    class="w-full"
  >
    <h1 class="text-xl text-center mb-2">
      {{ $t('mycountly.signup.new-password') }}
    </h1>
    <p class="text-sm text-center text-neutral-500 mb-6">
      {{ $t('mycountly.signup.create-your-new-account-password') }}
    </p>
    <cly-request
      disable-loading-icon
      silent-error
      @success="onResetPasswordSuccess"
      @error="onResetPasswordError"
    >
      <template #default="request">
        <cly-form>
          <template #default="v">
            <cly-form-step
              id="resetPasswordStep"
              @enter="handleEnter(request, v, 'reset')"
            >
              <cly-form-field
                vid="password"
                :rules="'required|min:8|max:32|one_number|one_uppercase|one_special|confirmed:password_confirmation'"
                :label="$t('mycountly.signup.password')"
              >
                <el-input
                  v-model="newPassword"
                  type="password"
                  class="w-full mt-1"
                  :placeholder="$t('mycountly.signup.placeholder.enter-your-password')"
                />
              </cly-form-field>
              <cly-form-field
                :rules="'required|min:8|max:32|confirmed:password'"
                vid="password_confirmation"
                :label="$t('mycountly.signup.confirm-password')"
              >
                <el-input
                  v-model="newPasswordConfirm"
                  type="password"
                  class="w-full mt-1"
                  :placeholder="$t('mycountly.signup.placeholder.confirm-your-password')"
                />
              </cly-form-field>
              <myc-big-button
                :disabled="!v.isSubmissionAllowed || request.pending"
                type="success"
                class="w-full !mt-5"
                @clicked="request.submit(getResetPasswordSubmission)"
              >
                {{ $t('mycountly.signup.reset-my-password') }}
              </myc-big-button>
            </cly-form-step>
          </template>
        </cly-form>
      </template>
    </cly-request>
  </div>
</template>

<script>
import { mapActions } from 'pinia';
import { useAuthStore } from '@/stores';
import { PreloginNavigationMixin } from '@/utils/mixins/prelogin-navigation';
import ClyFormField from '@/components/countly/lib-components/form/cly-form-field.vue';
import ClyForm from '@/components/countly/lib-components/form/cly-form.vue';
import ClyFormStep from '@/components/countly/lib-components/form/cly-form-step.vue';
import CheckCircleIcon from '@/components/icons/check-circle.vue';
import MycBigButton from '@/components/common/myc-big-button.vue';

export default {
  components: {
    MycBigButton,
    ClyForm,
    ClyFormStep,
    ClyFormField,
    CheckCircleIcon,
  },
  mixins: [PreloginNavigationMixin],
  data() {
    return {
      email: '',
      /*
        Steps:
        1. 'getEmail' - it shows the form to get the password reset email
        2. 'mailSent' - it's informs user that the email has been sent
        3. 'resetPassword' - it shows the form to reset the password
      */
      step: 'getEmail',
      submissionAllowed: true,
      newPassword: '',
      newPasswordConfirm: '',
      token: '',
    };
  },
  async created() {
    this.tryResuming(this.$router);
    if (this.$route.query.resetPassword) {
      this.step = 'resetPassword';
      if (this.$route.query.token) {
        this.token = decodeURIComponent(this.$route.query.token);
        const response = await this.submitVerifyPwdResetToken({ token: this.token });
        if (!response.isOk()) {
          this.step = 'getEmail';
          this.$clyNotify({ message: this.$t('mycountly.errors.invalid-password-reset-code'), sticky: false, type: 'error' });
        }
      } else {
        this.$clyNotify({ message: this.$t('mycountly.errors.missing-password-reset-code'), sticky: false, type: 'error' });
        // navigate to first step if code isn't provided
        this.step = 'getEmail';
      }
    }
    if (this.$route.query.mailSent) {
      this.step = 'mailSent';
    }
  },
  methods: {
    ...mapActions(useAuthStore, ['submitSendResetPasswordEmail', 'submitResetPassword', 'submitVerifyPwdResetToken']),
    getSendLinkSubmission() {
      return this.submitSendResetPasswordEmail({ email: this.email });
    },
    onSendLinkSuccess() {
      this.step = 'mailSent';
    },
    onSendLinkError(error) {
      this.$clyNotify({ message: error.message, sticky: false, type: 'error' });
    },
    getResetPasswordSubmission() {
      return this.submitResetPassword({ token: this.token, password: this.newPassword });
    },
    onResetPasswordSuccess() {
      this.$router.push('/account/signin');
    },
    onResetPasswordError(error) {
      this.$clyNotify({ message: error.message, sticky: false, type: 'error' });
    },
    handleEnter(request, v, type) {
      if (request.pending || !v.isSubmissionAllowed) {
        return;
      }

      if (type === 'reset') request.submit(this.getResetPasswordSubmission);
      if (type === 'send') request.submit(this.getSendLinkSubmission);
    },
  },
};
</script>
